import { useEffect, useState } from "react";
import { useSharedValue } from "react-native-reanimated";
import GatheringPrivacy from "src/api/graphql/__generated__/enums/GatheringPrivacy";
import GatheringViewMethod from "src/api/graphql/__generated__/enums/GatheringViewMethod";
import { NativeHeaderGradient } from "src/components/GatheringHeader";
import MarkGatheringViewed from "src/components/MarkGatheringViewed";
import NativeBackgroundColorWrapper from "src/components/NativeBackgroundColorWrapper";
import { PaletteWrapper } from "src/contexts/PaletteContext";
import { GatheringPreviewSubPageProps } from "../../types";
import GatheringPreviewPopUp from "./components/GatheringPreviewPopUp";
import GatheringPreviewPublicHeader from "./components/GatheringPreviewPublicHeader";
import GatheringPreviewPublicPhotos from "./components/GatheringPreviewPublicPhotos";
import GatheringPreviewPublicScrollWrapper from "./components/GatheringPreviewPublicScrollWrapper";
import GatheringPreviewPublicContext from "./contexts/GatheringPreviewPublicContext";
import useGatheringPublicPreviewInfo from "./hooks/useGatheringPublicPreviewInfo";
import { createGatheringPreviewPublicStore } from "./stores/gatheringPreviewPublicStore";

const GatheringPreviewPublic = ({
	gathering: { refCode, name, gatheringId, palette, stickerPack, thumbnail },
	handleJoinGathering,
	joinLoading,
	overrideNoPublicPreview,
}: GatheringPreviewSubPageProps & {
	overrideNoPublicPreview?: () => void;
}) => {
	const { gathering, refetch } = useGatheringPublicPreviewInfo(gatheringId, {
		fetchPolicy: "cache-and-network",
	});

	useEffect(() => {
		// If the web cache is out of date and this actually isn't a previewable album, then we want to fallback to private
		if (gathering?.privacy && !gathering?.privacy?.includes(GatheringPrivacy.PublicPreview)) {
			overrideNoPublicPreview?.();
		}
	}, [gathering?.privacy, overrideNoPublicPreview]);

	const [store] = useState(() =>
		createGatheringPreviewPublicStore({
			refCode,
			gatheringId,
			handleJoinGathering,
			joinLoading,
			gatheringName: name ?? gathering?.name ?? undefined,
		}),
	);
	useEffect(() => {
		store.setState({
			handleJoinGathering,
			joinLoading,
			gatheringName: name ?? gathering?.name ?? undefined,
		});
	}, [name, gathering?.name, handleJoinGathering, joinLoading, store]);
	const scrollOffset = useSharedValue(0);

	return (
		<>
			<MarkGatheringViewed
				gatheringId={gatheringId}
				method={GatheringViewMethod.PublicPreview}
			/>
			<PaletteWrapper palette={palette} syncBackgroundColor>
				<NativeBackgroundColorWrapper>
					<GatheringPreviewPublicContext.Provider value={{ store }}>
						<GatheringPreviewPublicScrollWrapper
							onRefresh={async () => {
								await refetch();
							}}
							onScroll={(event) => {
								scrollOffset.value = event.scrollY;
							}}
						>
							<GatheringPreviewPublicHeader
								scrollOffset={scrollOffset}
								thumbnail={thumbnail}
							/>
							<GatheringPreviewPublicPhotos
								stickerPack={gathering?.stickerPack ?? stickerPack}
							/>
						</GatheringPreviewPublicScrollWrapper>
						<GatheringPreviewPopUp />
						<NativeHeaderGradient />
					</GatheringPreviewPublicContext.Provider>
				</NativeBackgroundColorWrapper>
			</PaletteWrapper>
		</>
	);
};
export default GatheringPreviewPublic;
